import React, { useState, useEffect, useCallback, useMemo } from 'react';
import useLatestCallback from '@/hooks/useLatestCallback';
import { useOutletContext, useNavigate, useLocation } from 'react-router-dom';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Switch } from '@/components/ui/switch';
import { toast } from 'sonner';
import ImageUpload from '@/components/shared/ImageUpload';
import BatchOutPanel from '@/components/merchant/BatchOutPanel';
import {
  ArrowLeft, Wifi, QrCode, Monitor, FileText, Layers, Receipt,
  Info, CreditCard, CheckCircle2, XCircle,
  Eye, Upload, Trash2, Save, RefreshCw,
  Check, Percent
} from 'lucide-react';

const API = process.env.REACT_APP_BACKEND_URL;

const TABS = [
  { id: 'terminals', label: 'Payment Terminals', icon: Wifi },
  { id: 'checkout', label: 'Checkout Payment Setup', icon: QrCode },
  { id: 'qr', label: 'Universal Payment QR', icon: FileText },
  { id: 'batches', label: 'Batches', icon: Layers },
  { id: 'statements', label: 'Merchant Statements', icon: Receipt },
];

// In the back office the session comes from the layout's outlet; the Admin PWA
// (apps/admin, Payment Config) passes its restaurant account instead.
export default function PaymentSettingsPage({ account = null, displaySettingsHref = null }) {
  const outlet = useOutletContext() || {};
  const token = account ? account.token : outlet.token;
  const manager = account ? account.manager : outlet.manager;
  const extraHeaders = account?.headers;
  const navigate = useNavigate();
  const location = useLocation();
  const [activeTab, setActiveTab] = useState('terminals');
  // The customer display (2nd screen) has one home: Settings -> Display.
  const displaySettingsPath = displaySettingsHref
    || `${location.pathname.replace(/\/payment-setup\/?$/, '')}/settings?tab=display`;
  const restaurantId = manager?.restaurant_id;
  const headers = useMemo(() => ({ 'Content-Type': 'application/json', ...(extraHeaders || {}), Authorization: `Bearer ${token}` }),
    [token, extraHeaders]);

  return (
    <div className="max-w-5xl mx-auto" data-testid="payment-settings-page">
      {/* Header */}
      <div className="flex items-center justify-between mb-1">
        <div className="flex items-center gap-3">
          <button onClick={() => navigate(-1)} className="text-slate-400 hover:text-slate-600" data-testid="back-btn">
            <ArrowLeft className="w-5 h-5" />
          </button>
          <div>
            <h1 className="text-2xl font-bold text-slate-900 dark:text-white">Payment Settings</h1>
            <p className="text-sm text-slate-500">Manage merchant info, payment methods, and batch settlements</p>
          </div>
        </div>
        {displaySettingsHref !== false && (
          <Button variant="outline" size="sm" onClick={() => navigate(displaySettingsPath)} data-testid="customer-display-settings-link">
            <Monitor className="w-4 h-4 mr-2" /> Customer Display settings
          </Button>
        )}
      </div>

      {/* Tabs */}
      <div className="flex gap-1 border-b border-slate-200 dark:border-slate-700 mt-4 mb-6 overflow-x-auto">
        {TABS.map(tab => (
          <button
            key={tab.id}
            onClick={() => setActiveTab(tab.id)}
            className={`flex items-center gap-1.5 px-3 py-2.5 text-xs font-medium whitespace-nowrap border-b-2 transition-colors ${
              activeTab === tab.id
                ? 'border-teal-600 text-teal-700 dark:text-teal-400'
                : 'border-transparent text-slate-500 hover:text-slate-700 dark:hover:text-slate-300'
            }`}
            data-testid={`tab-${tab.id}`}
          >
            <tab.icon className="w-3.5 h-3.5" />
            {tab.label}
          </button>
        ))}
      </div>

      {/* Tab Content */}
      {activeTab === 'terminals' && <TerminalsTab headers={headers} />}
      {activeTab === 'checkout' && <CheckoutTab restaurantId={restaurantId} headers={headers} />}
      {activeTab === 'qr' && <UniversalQRTab restaurantId={restaurantId} headers={headers} />}
      {activeTab === 'batches' && <BatchOutPanel token={token} restaurantId={restaurantId} />}
      {activeTab === 'statements' && <StatementsTab />}
    </div>
  );
}

/* ========== shared: Aster Gateway settings ========== */
const ROLE_LABELS = {
  pos_register: 'POS register',
  kiosk: 'Kiosk',
  online: 'Online orders',
};

function feeProgramText(pricing) {
  if (!pricing || !pricing.model) return null;
  const model = String(pricing.model).toLowerCase();
  if (model === 'cash_discount' && pricing.cash_discount_percent != null) {
    return { text: `Cash discount ${Number(pricing.cash_discount_percent).toFixed(2)}%`, note: 'shown on customer receipts' };
  }
  if (model === 'surcharge' && pricing.surcharge_percent != null) {
    return { text: `Surcharge ${Number(pricing.surcharge_percent).toFixed(2)}%`, note: 'shown on customer receipts' };
  }
  if (model === 'flat_rate' && pricing.flat_rate_percent != null) {
    return { text: `Flat rate ${Number(pricing.flat_rate_percent).toFixed(2)}%`, note: 'paid by the merchant, not shown to customers' };
  }
  return { text: model.replace(/_/g, ' '), note: '' };
}

function usePaymentSettings(headers) {
  const [data, setData] = useState(null);
  const [error, setError] = useState(null);
  const [loading, setLoading] = useState(true);

  const apply = (body) => { setData(body); setError(null); };
  const request = useCallback(async (path = '', options = {}) => {
    const res = await fetch(`${API}/api/merchant/payment-settings${path}`, { headers, ...options });
    const body = await res.json().catch(() => null);
    if (!res.ok || !body) throw new Error(typeof body?.detail === 'string' ? body.detail : 'Payment settings are unavailable right now');
    return body;
  }, [headers]);

  const load = useCallback(async () => {
    setLoading(true);
    try { apply(await request()); } catch (e) { setError(e.message); }
    setLoading(false);
  }, [request]);

  useEffect(() => { load(); }, [load]);
  return { data, setData: apply, error, loading, request, reload: load };
}

function StatusPill({ status }) {
  const s = (status || 'unknown').toLowerCase();
  const style = s === 'online' ? 'bg-green-100 text-green-700' : s === 'offline' ? 'bg-red-100 text-red-600' : 'bg-slate-100 text-slate-500';
  return <span className={`text-xs font-medium px-2 py-0.5 rounded-full capitalize ${style}`}>{s}</span>;
}

function Field({ label, value }) {
  return (
    <div>
      <p className="text-[11px] uppercase tracking-wide text-slate-400">{label}</p>
      <p className="text-sm text-slate-800 dark:text-slate-200 font-mono break-all">{value || '—'}</p>
    </div>
  );
}

/* ========== TAB 1: Payment Terminals ========== */
function TerminalsTab({ headers }) {
  const { data, setData, error, loading, request } = usePaymentSettings(headers);
  const [refreshing, setRefreshing] = useState(false);
  const [kiosks, setKiosks] = useState([]);

  useEffect(() => {
    fetch(`${API}/api/kiosk-admin/kiosks`, { headers })
      .then(r => (r.ok ? r.json() : []))
      .then(list => setKiosks(Array.isArray(list) ? list.filter(k => k.paired) : []))
      .catch(() => setKiosks([]));
  }, [headers]);

  const refresh = async () => {
    setRefreshing(true);
    try {
      const body = await request('/refresh', { method: 'POST' });
      setData(body);
      if (body.connectivity_error) toast.warning(`Terminal status unavailable: ${body.connectivity_error}`);
      else toast.success('Synced from Aster Gateway');
    } catch (e) { toast.error(e.message); }
    setRefreshing(false);
  };

  const updateTerminal = async (terminal, patch) => {
    try {
      const updated = await request(`/terminals/${terminal.id}`, { method: 'PUT', body: JSON.stringify(patch) });
      setData({ ...data, terminals: data.terminals.map(t => (t.id === updated.id ? updated : t)) });
      toast.success('Terminal updated');
    } catch (e) { toast.error(e.message); }
  };

  if (loading) return <Spinner />;
  if (error) return <p className="text-sm text-red-600" data-testid="terminals-error">{error}</p>;

  const merchant = data.merchant;
  const defaultProcessor = merchant?.processors?.find(p => p.is_default) || merchant?.processors?.[0];
  const fee = feeProgramText(defaultProcessor?.pricing);
  const active = data.terminals.filter(t => t.is_active);
  const retired = data.terminals.filter(t => !t.is_active);

  if (!data.connected) {
    return (
      <Card data-testid="no-terminal-empty">
        <CardContent className="py-12 flex flex-col items-center text-center">
          <div className="w-12 h-12 rounded-full bg-slate-100 flex items-center justify-center mb-3">
            <CreditCard className="w-6 h-6 text-slate-400" />
          </div>
          <h3 className="font-semibold text-slate-900">Card payments are not set up</h3>
          <p className="text-sm text-slate-500 mt-1 max-w-md">
            Your card terminals appear here once your account is connected to Aster Gateway. Contact chain29 support to get set up.
          </p>
        </CardContent>
      </Card>
    );
  }

  return (
    <div className="space-y-4" data-testid="terminals-tab">
      <Card>
        <CardContent className="pt-6">
          <div className="flex items-start justify-between gap-4">
            <div>
              <div className="flex items-center gap-2">
                <h3 className="font-semibold text-slate-900 dark:text-white">{merchant.merchant_name || 'Merchant account'}</h3>
                <span className="inline-flex items-center gap-1 text-xs font-medium px-2 py-0.5 rounded-full bg-green-100 text-green-700">
                  <CheckCircle2 className="w-3 h-3" /> Aster Gateway connected
                </span>
              </div>
              <p className="text-xs text-slate-500 mt-1">
                Last synced {merchant.synced_at ? new Date(merchant.synced_at).toLocaleString() : 'never'}
              </p>
            </div>
            <Button variant="outline" onClick={refresh} disabled={refreshing} data-testid="refresh-terminals-btn">
              <RefreshCw className={`w-4 h-4 mr-2 ${refreshing ? 'animate-spin' : ''}`} /> Refresh
            </Button>
          </div>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mt-4">
            <Field label="Gateway account ID" value={merchant.hub_mid} />
            <Field label="Merchant ID" value={defaultProcessor?.processor_mid} />
            <Field label="Terminal ID" value={defaultProcessor?.terminal_id} />
            <Field label="Account status" value={merchant.status} />
            <div className="col-span-2">
              <p className="text-[11px] uppercase tracking-wide text-slate-400">Fee program</p>
              <p className="text-sm text-slate-800 dark:text-slate-200">
                {fee ? <>{fee.text}{fee.note && <span className="text-slate-500"> — {fee.note}</span>}</> : '—'}
              </p>
            </div>
          </div>
        </CardContent>
      </Card>

      {active.length === 0 && (
        <Card><CardContent className="py-8 text-center text-sm text-slate-500">No card terminals are listed on your account yet.</CardContent></Card>
      )}

      {active.map(t => (
        <TerminalCard key={t.id} terminal={t} onUpdate={updateTerminal} capabilities={merchant.capabilities} kiosks={kiosks} />
      ))}

      {retired.length > 0 && (
        <p className="text-xs text-slate-400">{retired.length} terminal(s) no longer listed by Aster Gateway are hidden.</p>
      )}

      <div className="rounded-lg border border-blue-200 bg-blue-50 p-4 flex gap-3" data-testid="terminal-config-info">
        <Info className="w-5 h-5 text-blue-500 flex-shrink-0 mt-0.5" />
        <p className="text-xs text-blue-700 leading-relaxed">
          Card terminals are connected to Aster Gateway by chain29 support. Here you choose where each one takes money:
          a card terminal at a POS register or a kiosk, and the virtual terminal for online orders. Quote your gateway account ID when you contact support.
        </p>
      </div>
    </div>
  );
}

function TerminalCard({ terminal: t, onUpdate, capabilities, kiosks }) {
  const [station, setStation] = useState(t.station || '');
  const isVirtual = t.terminal_type === 'virtual';
  const roles = isVirtual ? ['online'] : ['pos_register', 'kiosk'];

  return (
    <Card data-testid={`terminal-${t.id}`}>
      <CardContent className="pt-6">
        <div className="flex items-start justify-between gap-3">
          <div className="flex items-center gap-3">
            <div className={`w-10 h-10 rounded-lg flex items-center justify-center ${isVirtual ? 'bg-purple-100' : 'bg-teal-100'}`}>
              {isVirtual ? <Monitor className="w-5 h-5 text-purple-600" /> : <CreditCard className="w-5 h-5 text-teal-600" />}
            </div>
            <div>
              <p className="font-semibold text-sm text-slate-900 dark:text-white">{t.name || (isVirtual ? 'Virtual Terminal' : 'Card terminal')}</p>
              <p className="text-xs text-slate-500">{isVirtual ? 'Virtual Terminal' : 'Card terminal'}</p>
            </div>
          </div>
          {!isVirtual && <StatusPill status={t.connectivity_status} />}
        </div>

        <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mt-4">
          {!isVirtual && <Field label="Equipment" value={t.equipment} />}
          <Field label="Merchant ID" value={t.mid} />
          <Field label="Terminal ID" value={t.tpn || t.epi} />
          {!isVirtual && <Field label="Serial No." value={t.serial} />}
        </div>

        {isVirtual && capabilities && (
          <div className="flex flex-wrap gap-2 mt-4">
            {[['ecommerce', 'Online card payments'], ['recurring', 'Saved cards / recurring'], ['refund', 'Refunds'], ['void', 'Voids']].map(([key, label]) => (
              <span key={key} className={`text-xs px-2 py-1 rounded-full ${capabilities[key] ? 'bg-green-50 text-green-700' : 'bg-slate-100 text-slate-400 line-through'}`}>{label}</span>
            ))}
          </div>
        )}

        <div className="flex flex-wrap items-end gap-3 mt-5 pt-4 border-t border-slate-100 dark:border-slate-700">
          <div>
            <p className="text-[11px] uppercase tracking-wide text-slate-400 mb-1">Used for</p>
            <select
              className="h-9 rounded-md border border-slate-200 bg-white dark:bg-slate-800 px-2 text-sm"
              value={t.role || ''}
              onChange={(e) => onUpdate(t, { role: e.target.value })}
              data-testid={`terminal-role-${t.id}`}
            >
              <option value="">Not assigned</option>
              {roles.map(r => <option key={r} value={r}>{ROLE_LABELS[r]}</option>)}
            </select>
          </div>
          {t.role === 'kiosk' && (
            <div>
              <p className="text-[11px] uppercase tracking-wide text-slate-400 mb-1">Kiosk</p>
              <select
                className="h-9 rounded-md border border-slate-200 bg-white dark:bg-slate-800 px-2 text-sm"
                value={t.kiosk_id || ''}
                onChange={(e) => e.target.value && onUpdate(t, { kiosk_id: Number(e.target.value) })}
                data-testid={`terminal-kiosk-${t.id}`}
              >
                <option value="">{kiosks.length ? 'Choose the kiosk' : 'No kiosk set up yet'}</option>
                {kiosks.map(k => <option key={k.id} value={k.id}>{k.name}</option>)}
              </select>
            </div>
          )}
          {!isVirtual && (
            <div className="flex items-end gap-2">
              <div>
                <p className="text-[11px] uppercase tracking-wide text-slate-400 mb-1">Station</p>
                <Input value={station} onChange={(e) => setStation(e.target.value)} placeholder="Front counter" className="h-9 w-48" data-testid={`terminal-station-${t.id}`} />
              </div>
              {station !== (t.station || '') && (
                <Button size="sm" onClick={() => onUpdate(t, { station })}><Check className="w-4 h-4" /></Button>
              )}
            </div>
          )}
        </div>
      </CardContent>
    </Card>
  );
}

/* ========== TAB 2: Checkout Payment Setup ========== */
function CheckoutTab({ restaurantId, headers }) {
  const { data, error, loading, request, setData } = usePaymentSettings(headers);
  const [form, setForm] = useState(null);
  const [qrMethods, setQrMethods] = useState({
    zelle: { enabled: false, uploaded: false, url: '' },
    venmo: { enabled: false, uploaded: false, url: '' },
    crypto: { enabled: false, uploaded: false, url: '' },
  });
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (data) {
      setForm({
        tax_rate_percent: data.checkout.tax_rate_percent ?? '',
        tips_enabled: data.checkout.tips_enabled,
        tip_presets: (data.checkout.tip_presets || []).join(', '),
        cash_enabled: data.checkout.cash_enabled,
        receipt_printer_id: data.checkout.receipt_printer_id || '',
      });
    }
  }, [data]);

  const loadQrMethods = useLatestCallback(() => {
    if (!restaurantId) return;
    (async () => {
      try {
        const res = await fetch(`${API}/api/display/payment-qr/${restaurantId}`, { headers });
        if (res.ok) {
          const c = (await res.json()).config || {};
          setQrMethods({
            zelle: { enabled: !!c.zelle_qr_url, uploaded: !!c.zelle_qr_url, url: c.zelle_qr_url || '' },
            venmo: { enabled: !!c.venmo_qr_url, uploaded: !!c.venmo_qr_url, url: c.venmo_qr_url || '' },
            crypto: { enabled: !!c.crypto_qr_url, uploaded: !!c.crypto_qr_url, url: c.crypto_qr_url || '' },
          });
        }
      } catch { /* QR codes stay empty */ }
    })();
  });
  useEffect(() => {
    loadQrMethods();
  }, [restaurantId, loadQrMethods]);

  const toggleQR = (key) => setQrMethods(prev => ({ ...prev, [key]: { ...prev[key], enabled: !prev[key].enabled } }));
  const setQRUrl = (key, url) => setQrMethods(prev => ({ ...prev, [key]: { ...prev[key], url, uploaded: !!url } }));
  const clearQR = (key) => setQrMethods(prev => ({ ...prev, [key]: { url: '', uploaded: false, enabled: false } }));

  const saveAll = async () => {
    const presets = form.tip_presets.split(',').map(s => s.trim()).filter(Boolean).map(Number);
    if (presets.some(n => !Number.isInteger(n))) { toast.error('Tip presets must be whole percentages'); return; }
    if (form.tax_rate_percent === '') { toast.error('Enter your sales tax rate'); return; }
    setSaving(true);
    try {
      setData(await request('/checkout', {
        method: 'PUT',
        body: JSON.stringify({
          tax_rate_percent: Number(form.tax_rate_percent),
          tips_enabled: form.tips_enabled,
          tip_presets: presets,
          cash_enabled: form.cash_enabled,
          receipt_printer_id: form.receipt_printer_id || '',
        }),
      }));
      if (restaurantId) {
        const qr = await fetch(`${API}/api/display/payment-qr/${restaurantId}`, {
          method: 'PUT', headers,
          body: JSON.stringify({
            zelle_qr_url: qrMethods.zelle.enabled ? qrMethods.zelle.url : '',
            venmo_qr_url: qrMethods.venmo.enabled ? qrMethods.venmo.url : '',
            crypto_qr_url: qrMethods.crypto.enabled ? qrMethods.crypto.url : '',
          }),
        });
        if (!qr.ok) throw new Error('QR codes were not saved');
      }
      toast.success('Settings saved');
    } catch (e) { toast.error(e.message); }
    setSaving(false);
  };

  if (loading || (!form && !error)) return <Spinner />;
  if (error) return <p className="text-sm text-red-600">{error}</p>;

  const defaultProcessor = data.merchant?.processors?.find(p => p.is_default) || data.merchant?.processors?.[0];
  const fee = feeProgramText(defaultProcessor?.pricing);
  const QR_ITEMS = [
    { key: 'zelle', label: 'Zelle', color: 'bg-purple-500', icon: '□' },
    { key: 'venmo', label: 'Venmo', color: 'bg-blue-500', icon: '□' },
    { key: 'crypto', label: 'Crypto', color: 'bg-orange-500', icon: 'B' },
  ];

  return (
    <div className="space-y-6" data-testid="checkout-tab">
      <Card>
        <CardContent className="pt-6 space-y-5">
          <h3 className="font-semibold text-slate-900 flex items-center gap-2">
            <CreditCard className="w-4 h-4 text-slate-400" /> Basic Payment Methods
          </h3>
          <div className="grid md:grid-cols-2 gap-3">
            <div className={`flex items-center justify-between p-4 rounded-xl border-2 ${form.cash_enabled ? 'border-green-400 bg-green-50' : 'border-slate-200'}`}>
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 rounded-xl bg-green-500 flex items-center justify-center text-white font-bold text-lg">$</div>
                <div><p className="font-semibold text-sm">Cash</p><p className="text-xs text-slate-500">Accept cash at the register</p></div>
              </div>
              <Switch checked={form.cash_enabled} onCheckedChange={(v) => setForm(f => ({ ...f, cash_enabled: v }))} data-testid="toggle-cash" />
            </div>
            <div className={`flex items-center justify-between p-4 rounded-xl border-2 ${data.connected ? 'border-blue-400 bg-blue-50' : 'border-slate-200'}`}>
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 rounded-xl bg-blue-500 flex items-center justify-center"><CreditCard className="w-5 h-5 text-white" /></div>
                <div>
                  <p className="font-semibold text-sm">Credit/Debit Card</p>
                  <p className="text-xs text-slate-500">{data.connected ? 'Through Aster Gateway' : 'Not set up — contact support to get connected'}</p>
                </div>
              </div>
              {data.connected ? <CheckCircle2 className="w-5 h-5 text-blue-500" /> : <XCircle className="w-5 h-5 text-slate-300" />}
            </div>
          </div>
          <div className="grid md:grid-cols-3 gap-4">
            <div>
              <p className="text-sm font-medium text-slate-700 mb-1">Sales tax rate</p>
              <div className="flex items-center gap-1">
                <Input type="number" step="0.001" min="0" max="30" value={form.tax_rate_percent}
                  onChange={(e) => setForm(f => ({ ...f, tax_rate_percent: e.target.value }))} className="w-28" data-testid="tax-rate-input" />
                <span className="text-sm text-slate-500">%</span>
              </div>
              {data.checkout.tax_rate_percent == null && <p className="text-xs text-red-500 mt-1">Required before the POS can check out</p>}
            </div>
            <div>
              <p className="text-sm font-medium text-slate-700 mb-1">Tips</p>
              <Switch checked={form.tips_enabled} onCheckedChange={(v) => setForm(f => ({ ...f, tips_enabled: v }))} data-testid="toggle-tips" />
            </div>
            <div>
              <p className="text-sm font-medium text-slate-700 mb-1">Tip presets (%)</p>
              <Input value={form.tip_presets} onChange={(e) => setForm(f => ({ ...f, tip_presets: e.target.value }))} placeholder="15, 18, 20" disabled={!form.tips_enabled} data-testid="tip-presets-input" />
            </div>
          </div>
          <div>
            <p className="text-sm font-medium text-slate-700 mb-1">Receipt printer</p>
            <select value={form.receipt_printer_id} onChange={(e) => setForm(f => ({ ...f, receipt_printer_id: e.target.value }))}
              className="h-10 rounded-md border border-slate-200 px-3 text-sm bg-white min-w-[240px]" data-testid="receipt-printer-select">
              <option value="">None - receipts are texted or e-mailed</option>
              {(data.receipt_printers || []).map(p => (
                <option key={p.id} value={p.id}>{p.name}{p.enabled ? '' : ' (switched off)'}</option>
              ))}
            </select>
            <p className="text-xs text-slate-500 mt-1">
              {(data.receipt_printers || []).length ? '"Print receipt" at checkout and on the kiosk sends here.' : 'Add a network printer under Kitchen → Printers first.'}
            </p>
          </div>
        </CardContent>
      </Card>

      <Card>
        <CardContent className="pt-6">
          <h3 className="font-semibold text-slate-900 mb-1 flex items-center gap-2"><Percent className="w-4 h-4 text-slate-400" /> Card Fee Program</h3>
          <p className="text-sm text-slate-700 mt-2">
            {fee ? <>{fee.text}{fee.note && <span className="text-slate-500"> — {fee.note}</span>}</> : 'No card fee program on your account.'}
          </p>
          <p className="text-xs text-slate-400 mt-1">Set when your account was boarded. Contact support to change it.</p>
        </CardContent>
      </Card>

      <Card>
        <CardContent className="pt-6">
          <h3 className="font-semibold text-slate-900 mb-1 flex items-center gap-2">
            <QrCode className="w-4 h-4 text-slate-400" /> QR Code Payment Methods
          </h3>
          <p className="text-xs text-slate-500 mb-4">Upload your payment QR codes so customers can scan and pay during checkout</p>
          <div className="space-y-3">
            {QR_ITEMS.map(item => {
              const m = qrMethods[item.key];
              return (
                <div key={item.key} className={`flex items-center justify-between p-4 rounded-xl border-2 ${m.enabled ? 'border-purple-200 bg-purple-50/30' : 'border-slate-100'}`} data-testid={`qr-${item.key}`}>
                  <div className="flex items-center gap-3">
                    <div className={`w-10 h-10 rounded-xl ${item.color} flex items-center justify-center text-white font-bold text-sm`}>{item.icon}</div>
                    <div>
                      <p className="font-semibold text-sm">{item.label}</p>
                      {m.uploaded
                        ? <p className="text-xs text-green-600 flex items-center gap-1"><CheckCircle2 className="w-3 h-3" /> QR code uploaded</p>
                        : <p className="text-xs text-slate-400">Upload QR code for customers to scan</p>}
                    </div>
                  </div>
                  <div className="flex items-center gap-2">
                    {m.uploaded ? (
                      <>
                        <a href={m.url} target="_blank" rel="noreferrer" className="w-8 h-8 rounded-full bg-slate-100 flex items-center justify-center hover:bg-slate-200" title="View"><Eye className="w-3.5 h-3.5 text-slate-500" /></a>
                        <button onClick={() => clearQR(item.key)} className="w-8 h-8 rounded-full bg-slate-100 flex items-center justify-center hover:bg-red-100" title="Delete"><Trash2 className="w-3.5 h-3.5 text-red-400" /></button>
                      </>
                    ) : (
                      <ImageUpload value={m.url} onChange={(url) => setQRUrl(item.key, url)} folder="payment-qr" label="" className="inline-block" />
                    )}
                    <Switch checked={m.enabled} onCheckedChange={() => toggleQR(item.key)} />
                  </div>
                </div>
              );
            })}
          </div>
        </CardContent>
      </Card>

      <div className="flex justify-end">
        <Button onClick={saveAll} disabled={saving} className="bg-teal-600 hover:bg-teal-700 text-white" data-testid="save-checkout-btn">
          <Save className="w-4 h-4 mr-2" /> {saving ? 'Saving...' : 'Save Settings'}
        </Button>
      </div>
    </div>
  );
}

/* ========== TAB 4: Universal Payment QR ========== */
function UniversalQRTab({ restaurantId, headers }) {
  const [form, setForm] = useState({
    venmo_username: '', zelle_email: '', solana_address: '',
  });
  const [saving, setSaving] = useState(false);

  const loadQrLabels = useLatestCallback(() => {
    (async () => {
      try {
        const rid = restaurantId || 'default';
        const res = await fetch(`${API}/api/display/payment-qr/${rid}`, { headers });
        if (res.ok) {
          const data = await res.json();
          const c = data.config || {};
          setForm(prev => ({
            ...prev,
            venmo_username: c.venmo_label || '',
            zelle_email: c.zelle_label || '',
            solana_address: c.crypto_label || '',
          }));
        }
      } catch { /* ignore */ }
    })();
  });
  useEffect(() => {
    loadQrLabels();
  }, [restaurantId, loadQrLabels]);

  const handleSave = async () => {
    setSaving(true);
    try {
      const rid = restaurantId || 'default';
      const res = await fetch(`${API}/api/display/payment-qr/${rid}`, {
        method: 'PUT', headers,
        body: JSON.stringify({
          venmo_label: form.venmo_username,
          zelle_label: form.zelle_email,
          crypto_label: form.solana_address,
        }),
      });
      if (!res.ok) throw new Error('not saved');
      toast.success('Payment settings saved');
    } catch { toast.error('Failed to save'); }
    setSaving(false);
  };

  return (
    <div className="space-y-6" data-testid="qr-tab">
      <Card>
        <CardContent className="pt-6 space-y-6">
          <div className="flex items-center gap-3 mb-2">
            <div className="w-10 h-10 rounded-xl bg-purple-100 flex items-center justify-center">
              <QrCode className="w-5 h-5 text-purple-600" />
            </div>
            <div>
              <h3 className="font-semibold text-slate-900">Universal Payment Gateway</h3>
              <p className="text-xs text-slate-500">Configure your payment handles for QR code payments. Customers can pay via any method.</p>
            </div>
          </div>

          {/* Venmo */}
          <div>
            <p className="font-semibold text-sm mb-2 flex items-center gap-2">
              <span className="w-6 h-6 rounded bg-blue-600 text-white text-xs flex items-center justify-center font-bold">V</span> Venmo Username
            </p>
            <div className="flex items-center gap-0 border rounded-lg overflow-hidden">
              <span className="px-3 bg-slate-50 text-slate-400 text-sm border-r py-2">@</span>
              <Input value={form.venmo_username} onChange={(e) => setForm(f => ({ ...f, venmo_username: e.target.value }))} placeholder="yourusername" className="border-0 focus-visible:ring-0" data-testid="venmo-username" />
            </div>
            <p className="text-xs text-slate-400 mt-1">Your Venmo @username for receiving payments</p>
          </div>

          {/* Zelle */}
          <div className="rounded-lg border border-purple-200 bg-purple-50/30 p-4">
            <p className="font-semibold text-sm mb-3 flex items-center gap-2">
              <span className="w-6 h-6 rounded bg-purple-600 text-white text-xs flex items-center justify-center font-bold">Z</span> Zelle Information
            </p>
            <div>
              <label className="text-xs font-medium text-slate-700 block mb-1">Email or phone</label>
              <Input value={form.zelle_email} onChange={(e) => setForm(f => ({ ...f, zelle_email: e.target.value }))} placeholder="your@email.com" data-testid="zelle-email" />
            </div>
            <p className="text-xs text-slate-400 mt-2">Customers will see this info to send Zelle payments</p>
          </div>

          {/* Solana */}
          <div className="rounded-lg border border-orange-200 bg-orange-50/30 p-4">
            <p className="font-semibold text-sm mb-2 flex items-center gap-2">
              <span className="w-6 h-6 rounded bg-orange-500 text-white text-xs flex items-center justify-center font-bold">S</span> Solana Wallet Address
              <span className="text-xs bg-orange-100 text-orange-600 px-1.5 py-0.5 rounded font-medium">Crypto</span>
            </p>
            <Input value={form.solana_address} onChange={(e) => setForm(f => ({ ...f, solana_address: e.target.value }))} placeholder="Your Solana wallet address (e.g., 7xKXtg2CW87...)" data-testid="solana-address" />
            <p className="text-xs text-slate-400 mt-1">For receiving SOL and USDC payments. Get a wallet from <a href="https://phantom.app" target="_blank" rel="noreferrer" className="text-purple-600 hover:underline">Phantom</a></p>
          </div>

          <div className="border-t pt-4 flex justify-center">
            <Button onClick={handleSave} disabled={saving} className="bg-slate-900 hover:bg-slate-800 text-white px-8" data-testid="save-qr-settings">
              <Save className="w-4 h-4 mr-2" /> {saving ? 'Saving...' : 'Save Payment Settings'}
            </Button>
          </div>
        </CardContent>
      </Card>

      {/* How it works */}
      <div className="rounded-lg border border-blue-200 bg-blue-50 p-5">
        <h4 className="font-semibold text-sm mb-3">How it works:</h4>
        <ul className="space-y-1.5 text-xs text-slate-600">
          <li>When you generate a payment QR code, customers see all available options</li>
          <li>For Venmo/Zelle, they'll see your handles to send payment</li>
          <li>For crypto, they can scan with any Solana wallet (Phantom, Solflare, etc.)</li>
          <li>All transactions are recorded on the Solana blockchain for transparency</li>
        </ul>
      </div>
    </div>
  );
}

/* ========== TAB 6: Merchant Statements ========== */
function StatementsTab() {
  return (
    <div className="space-y-4" data-testid="statements-tab">
      <Card>
        <CardContent className="py-16 text-center">
          <Receipt className="w-12 h-12 text-slate-300 mx-auto mb-3" />
          <h3 className="font-semibold text-slate-900">Merchant Statements</h3>
          <p className="text-sm text-slate-500 mt-1">Statements will appear here once batch settlements are processed.</p>
        </CardContent>
      </Card>
    </div>
  );
}

function Spinner() {
  return <div className="flex items-center justify-center py-8"><div className="w-6 h-6 border-2 border-slate-300 border-t-slate-600 rounded-full animate-spin" /></div>;
}
